import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { signOutAndForgetDevice } from "./sign-out";

/**
 * The Sign out button's action: sign out, then go to "/".
 *
 * Staying on the page was the bug. A signed-out event page offers "Sign in to
 * join" with a redirect back to itself, and every other gated page renders the
 * sign-in screen in place, so the next person to sign in on this browser
 * landed on the page the last one left: after a withdrawal, that event's
 * registration page. At "/" the gate renders the sign-in screen with no
 * destination of its own, and the landing picks All Events or My Events.
 *
 * The auth gate and a member's event page sign out through here. A
 * signed-out visit to a shared event link or an invite keeps its redirect,
 * and so does the event page's non-member preview: its sign-out stays on the
 * page, so somebody signed in as the wrong account can switch and come back.
 * A withdrawal on that page is the exception: the preview it leaves behind
 * signs out through here.
 *
 * The navigation waits for the sign-out: sent to "/" while still signed in,
 * the landing would forward to /events and the sign-in screen would then
 * render there. `finally`, so a failed sign-out still leaves the page.
 */
export function useSignOutToLanding(): (uid: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (uid: string) => {
      void signOutAndForgetDevice(uid).finally(
        () => void navigate({ to: "/", replace: true }),
      );
    },
    [navigate],
  );
}
